import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef
} from "react";
import styles from "./TimeSelectionView.module.css";
import DayPartRow from "./DayPartRow";
import { DayPartRowType, SelectedDayPartPair } from "./types";
import { DayPart } from "../../gql/types";
import xButtonIcon from '../../assets/icons/xSmallIcon.svg';
import ErrorUnit from "../input/ErrorUnit";

export interface TimeSelectionEditDayPartTypeViewProps {
    initialSelectedDayParts?: SelectedDayPartPair[];
    onEditChange?: (data: SelectedDayPartPair[]) => void;
    onExploreMore?: () => void;
    errorStr?: string;
}

export interface TimeSelectionEditDayPartTypeViewHandle {
    validate: () => { isValid: boolean, selected: SelectedDayPartPair[] };
}

type ViewMode = "PRIMARY" | "MODIFY" | "REMOVE";

const TimeSelectionEditDayPartTypeView = forwardRef<
    TimeSelectionEditDayPartTypeViewHandle,
    TimeSelectionEditDayPartTypeViewProps
>(({ initialSelectedDayParts, onEditChange, onExploreMore, errorStr }, ref) => {
    const aggregateRows = (pairs: SelectedDayPartPair[] | undefined): DayPartRowType[] => {
        if (!pairs || pairs.length === 0) return [];
        const grouped: Record<string, DayPart[]> = {};
        pairs.forEach(({ date, dayPart }) => {
            if (!grouped[date]) {
                grouped[date] = [];
            }
            if (!grouped[date].includes(dayPart)) {
                grouped[date].push(dayPart);
            }
        });
        let idCounter = 1;
        return Object.entries(grouped).map(([date, dayParts]) => ({
            id: idCounter++,
            date,
            selectedDayParts: dayParts,
        }));
    };

    const flattenRows = (rows: DayPartRowType[]): SelectedDayPartPair[] =>
        rows.flatMap((row) =>
            row.selectedDayParts.map((dp) => ({ date: row.date, dayPart: dp }))
        );

    const [rows, setRows] = useState<DayPartRowType[]>(aggregateRows(initialSelectedDayParts));
    const [viewMode, setViewMode] = useState<ViewMode>("MODIFY");
    // const [viewMode, setViewMode] = useState<ViewMode>(initialSelectedDayParts && initialSelectedDayParts.length > 0 ? "PRIMARY" : "MODIFY");
    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const showRowRemove = viewMode === "REMOVE";

    useEffect(() => {
        onEditChange && onEditChange(flattenRows(rows));
    }, [rows, onEditChange]);

    useEffect(() => {
        if (errorMessage === null || errorMessage.length === 0) {
            setErrorMessage(errorMessage);
        }
    }, [errorStr]);

    useImperativeHandle(ref, () => ({
        validate: (): { isValid: boolean; selected: SelectedDayPartPair[] } => {
            if (rows.length === 0) {
                setErrorMessage("Please add at least one date.");
                setShowErrors(true);
                return { isValid: false, selected: [] };
            }

            if (hasPastDate(rows)) {
                setErrorMessage("Please remove rows with dates in the past.");
                setShowErrors(true);
                return { isValid: false, selected: [] };
            }

            const hasAnySelected = rows.some((r) => r.selectedDayParts.length > 0);
            if (!hasAnySelected) {
                setErrorMessage("Please select at least one option.");
                setShowErrors(true);
                return { isValid: false, selected: [] };
            }

            const filtered = rows.filter((r) => r.selectedDayParts.length > 0);
            setRows(filtered);
            setErrorMessage(null);
            setShowErrors(false);
            return { isValid: true, selected: flattenRows(filtered) };
        }
    }));

    const handleRowChange = (
        id: number,
        field: keyof DayPartRowType,
        value: string | DayPart[]
    ) => {
        setRows((prev) => {
            const updated = prev.map((row) =>
                row.id === id ? { ...row, [field]: value } : row
            );
            return updated;
        });
    };

    const hasPastDate = (currentRows: DayPartRowType[]) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        return currentRows.some((row) => {
            const [year, month, day] = row.date.split("-").map(Number);
            const rowDay = new Date(year, month - 1, day);
            return rowDay < today;
        });
    };

    const removeRow = (id: number) => {
        setRows((prev) => {
            const updated = prev.filter((row) => row.id !== id);
            if (!hasPastDate(updated)) {
                setErrorMessage("");
                setShowErrors(false);
            }

            if (updated.length === 0) {
                setViewMode("MODIFY");
            }

            return updated;
        });
    };

    const addRow = () => {
        let defaultDate = new Date().toISOString().split("T")[0];
        while (rows.some((r) => r.date === defaultDate)) {
            const d = new Date(defaultDate);
            d.setDate(d.getDate() + 1);
            defaultDate = d.toISOString().split("T")[0];
        }
        const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
        const newRow: DayPartRowType = {
            id: newId,
            date: defaultDate,
            selectedDayParts: [],
        };
        setRows((prev) => [...prev, newRow]);
    };

    const renderButtonContainer = () => {
        switch (viewMode) {
            case "PRIMARY":
                return (
                    <div className={styles.buttonContainer}>
                        <button onClick={() => setViewMode("MODIFY")} className={styles.modifyManually}>
                            Modify Manually
                        </button>
                    </div>
                );
            case "MODIFY":
                return (
                    <div className={styles.buttonContainer}>
                        <button onClick={() => {
                            setErrorMessage(null);
                            setShowErrors(false);
                            addRow();
                        }}
                            className={styles.addRemoveRowButton}>
                            Add Date
                        </button>
                        {rows.length > 0 && <button onClick={() => setViewMode("REMOVE")} className={styles.addRemoveRowButton}>
                            Remove Date
                        </button>}
                        <button
                            onClick={() => {
                                const isValid = ref && typeof ref !== 'function' && ref.current?.validate().isValid;
                                if (isValid) {
                                    setViewMode("PRIMARY");
                                }
                            }}
                            className={styles.doneButton}
                        >
                            Done
                        </button>
                    </div>
                );
            case "REMOVE":
                return (
                    <div className={styles.buttonContainer}>
                        <button onClick={() => setViewMode("MODIFY")} className={styles.doneButton}>
                            Done
                        </button>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={`${styles.daySelectionTableContainer} ${styles.noPadding}`}>
            <div className={`${styles.daySelectionTable} ${showRowRemove ? styles.shiftLeft : ""}`}>
                {rows.map((row) => {
                    const showRowError = showErrors && row.selectedDayParts.length === 0;
                    return (
                        <div key={row.id} className={styles.dayPartEditRow}>
                            <DayPartRow
                                id={row.id}
                                date={row.date}
                                selectedDayParts={row.selectedDayParts}
                                onRowChange={handleRowChange}
                                enableDateSelection={true}
                                readOnly={viewMode !== "MODIFY"}
                                errorStr={showRowError ? "select at least one daypart icon." : undefined}
                            />
                            {showRowRemove && (
                                <button
                                    className={styles.individualRowRemoveButton}
                                    onClick={() => removeRow(row.id)}
                                >
                                    <img src={xButtonIcon} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            <ErrorUnit isFocused={false} errorStr={errorMessage ?? null} />
            {renderButtonContainer()}
        </div>
    );
});

export default TimeSelectionEditDayPartTypeView;
